import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Clock, ChevronDown, LogIn } from 'lucide-react';
import { companyInfo, services, cities } from '../../data/mock';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../ui/navigation-menu';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '../ui/sheet';
import { cn } from '../../lib/utils';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar */}
      <div className="bg-[hsl(215,25%,15%)] text-white py-2 text-sm hidden md:block">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[hsl(24,95%,53%)]" />
              Urgences 24h/24 – 7j/7
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href={`tel:${companyInfo.phone}`} className="hover:text-[hsl(24,95%,53%)] transition-colors">
              <Phone className="w-4 h-4 inline mr-2" />
              {companyInfo.phone}
            </a>
            <span className="text-gray-400">|</span>
            <a 
              href={`tel:${companyInfo.phoneEmergency}`} 
              className="text-[hsl(24,95%,53%)] font-semibold hover:text-[hsl(24,95%,60%)] transition-colors"
            >
              Urgences : {companyInfo.phoneEmergency}
            </a>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header 
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300",
          isScrolled ? "glass shadow-lg" : "bg-white"
        )}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={companyInfo.logo} 
                alt="NGL SERVICES" 
                className="h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden lg:flex">
              <NavigationMenuList className="gap-1">
                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/"
                      className={cn(
                        "nav-link px-4 py-2 text-[15px] font-medium hover:text-[hsl(207,78%,42%)] transition-colors",
                        isActive('/') && "text-[hsl(207,78%,42%)] active"
                      )}
                    >
                      Accueil
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-[15px] font-medium">
                    Nos Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[500px] gap-2 p-4 md:grid-cols-2">
                      {services.map((service) => (
                        <li key={service.id}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={`/services/${service.id}`}
                              className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-[hsl(207,78%,95%)] focus:bg-accent"
                            >
                              <div className="text-sm font-medium leading-none text-[hsl(215,25%,15%)]">
                                {service.title}
                              </div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                                {service.shortDesc}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-[15px] font-medium">
                    Zones d&apos;intervention
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-1 p-4 md:grid-cols-2">
                      {cities.map((city) => (
                        <li key={city.id}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={`/villes/${city.id}`}
                              className={cn(
                                "block select-none rounded-md p-2 text-sm leading-none no-underline outline-none transition-colors hover:bg-[hsl(207,78%,95%)]",
                                city.isPrimary && "font-semibold text-[hsl(207,78%,42%)]"
                              )}
                            >
                              {city.name}
                              {city.isPrimary && (
                                <span className="ml-2 text-xs text-[hsl(24,95%,53%)]">(Siège)</span>
                              )}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/a-propos"
                      className={cn(
                        "nav-link px-4 py-2 text-[15px] font-medium hover:text-[hsl(207,78%,42%)] transition-colors",
                        isActive('/a-propos') && "text-[hsl(207,78%,42%)] active"
                      )}
                    >
                      À propos
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuLink asChild>
                    <Link
                      to="/contact"
                      className={cn(
                        "nav-link px-4 py-2 text-[15px] font-medium hover:text-[hsl(207,78%,42%)] transition-colors",
                        isActive('/contact') && "text-[hsl(207,78%,42%)] active"
                      )}
                    >
                      Contact
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/login" data-testid="connexion-link-desktop">
                <Button
                  variant="outline"
                  className="rounded-full px-5 border-[hsl(207,78%,42%)] text-[hsl(207,78%,42%)] hover:bg-[hsl(207,78%,42%)] hover:text-white transition-colors"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Connexion
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button className="emergency-btn rounded-full px-5">
                  <Phone className="w-4 h-4 mr-2" />
                  Urgence 24h/24
                </Button>
              </a>
              <Link to="/contact">
                <Button className="btn-primary rounded-full px-5">
                  Devis gratuit
                </Button>
              </Link>
            </div>

            {/* Mobile Menu */}
            <Sheet>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <nav className="flex flex-col gap-4 mt-8">
                  <SheetClose asChild>
                    <Link to="/" className="text-lg font-medium py-2 border-b">
                      Accueil
                    </Link>
                  </SheetClose>
                  
                  <div className="py-2 border-b">
                    <p className="text-lg font-medium mb-2">Nos Services</p>
                    <div className="pl-4 flex flex-col gap-2">
                      {services.map((service) => (
                        <SheetClose asChild key={service.id}>
                          <Link 
                            to={`/services/${service.id}`}
                            className="text-sm text-muted-foreground hover:text-[hsl(207,78%,42%)]"
                          >
                            {service.title}
                          </Link>
                        </SheetClose>
                      ))}
                    </div>
                  </div>

                  <div className="py-2 border-b">
                    <p className="text-lg font-medium mb-2">Zones d&apos;intervention</p>
                    <div className="pl-4 flex flex-col gap-1 max-h-40 overflow-y-auto">
                      {cities.map((city) => (
                        <SheetClose asChild key={city.id}>
                          <Link 
                            to={`/villes/${city.id}`}
                            className={cn(
                              "text-sm hover:text-[hsl(207,78%,42%)]",
                              city.isPrimary ? "text-[hsl(207,78%,42%)] font-medium" : "text-muted-foreground"
                            )}
                          >
                            {city.name}
                          </Link>
                        </SheetClose>
                      ))}
                    </div>
                  </div>

                  <SheetClose asChild>
                    <Link to="/a-propos" className="text-lg font-medium py-2 border-b">
                      À propos
                    </Link>
                  </SheetClose>

                  <SheetClose asChild>
                    <Link to="/contact" className="text-lg font-medium py-2 border-b">
                      Contact
                    </Link>
                  </SheetClose>

                  <div className="flex flex-col gap-3 mt-4">
                    <SheetClose asChild>
                      <Link to="/login" data-testid="connexion-link-mobile">
                        <Button
                          variant="outline"
                          className="w-full rounded-full border-[hsl(207,78%,42%)] text-[hsl(207,78%,42%)] hover:bg-[hsl(207,78%,42%)] hover:text-white transition-colors"
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Connexion
                        </Button>
                      </Link>
                    </SheetClose>
                    <a href={`tel:${companyInfo.phoneEmergency}`}>
                      <Button className="w-full emergency-btn rounded-full">
                        <Phone className="w-4 h-4 mr-2" />
                        Urgence : {companyInfo.phoneEmergency}
                      </Button>
                    </a>
                    <SheetClose asChild>
                      <Link to="/contact">
                        <Button className="w-full btn-primary rounded-full">
                          Demander un devis
                        </Button>
                      </Link>
                    </SheetClose>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
